import { useInfiniteQuery } from '@tanstack/react-query';
import { UseDataQueryOptions } from '../../../../hooks/useDataQuery/dataQuery.types';
import useDataQuery from '../../../../hooks/useDataQuery/useDataQuery';
import {
  getKitCombinationsCountKey,
  getKitCombinationsKey,
  getKitDetailsKey,
} from './kitDetails.keys';
import kitDetailsRepository from './kitDetails.repository';
import {
  GetKitCombinationCount,
  GetKitCombinationsPageParams,
  KitDetails,
  UseGetKitCombinationsParams,
} from './kitDetails.types';

export const useGetKitDetails = (kitId: string, options?: UseDataQueryOptions) =>
  useDataQuery<KitDetails>(
    {
      queryKey: getKitDetailsKey(kitId),
      queryFn: async () => {
        const response = await kitDetailsRepository().getKitDetails(kitId);

        if (response.ok && response.data) {
          return response.data;
        }

        throw new Error('failed to fetch kit details');
      },
    },

    options,
  );

export const useGetKitCombinations = ({
  totalCount,
  options,
  teamId,
  ...params
}: UseGetKitCombinationsParams) =>
  useInfiniteQuery({
    queryKey: getKitCombinationsKey({ kitId: params.kitId, filters: params.filters, teamId }),
    initialPageParam: 0,
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      const filters = {
        ...params.filters,
        skip: pageParam * params.filters.limit,
      };
      const response = await kitDetailsRepository().getKitCombinationsPage({ ...params, filters });

      if (response.ok && response.data) {
        return { data: [...response.data], page: pageParam, totalCount };
      }

      throw new Error('failed to fetch kit combinations');
    },
    getNextPageParam: (lastPage, allPages) => {
      const limit = params.filters?.limit ?? 10;
      const loadedSoFar = allPages.length * limit;

      if (loadedSoFar >= totalCount) {
        return undefined;
      }
      return lastPage.page + 1;
    },
    ...options,
  });

export const useGetKitCombinationsCount = (
  params: GetKitCombinationsPageParams,
  options?: UseDataQueryOptions,
) =>
  useDataQuery<GetKitCombinationCount[]>(
    {
      queryKey: getKitCombinationsCountKey(params.kitId, params.filters),
      queryFn: async () => {
        const response = await kitDetailsRepository().getKitCombinationsCount(params);

        if (response.ok && response.data) {
          return response.data;
        }

        throw new Error('failed to fetch kit combinations count');
      },
    },

    options,
  );
